import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Nav,
  Form,
  InputGroup,
  Table,
  Dropdown,
} from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  viewPaymentAPI,
  getPaymentTransactionsAPI,
  getPaymentHistoryAPI,
} from "./payment-api";
import { FaArrowLeft, FaSearch } from "react-icons/fa";
import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import transactionIcon from "../../assets/svg/transactions.svg";
import profileIc from "../../assets/png/profile-ic.png";
import DownloadIc from "../../assets/png/downloads.png";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import ReactPaginate from "react-paginate";
import ProfileImageModal from "../common/common-preview";

const PaymentView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const customerId = location.state?.customerId; // Get customer ID from navigation state

  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("");
  const openFile = (fileUrl = "") => {
    if (!fileUrl) return;

    const cleanUrl = fileUrl.split("?")[0].toLowerCase();

    // 🖼 Images → popup
    if (
      cleanUrl.endsWith(".png") ||
      cleanUrl.endsWith(".jpg") ||
      cleanUrl.endsWith(".jpeg") ||
      cleanUrl.endsWith(".gif") ||
      cleanUrl.endsWith(".webp")
    ) {
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📄 PDF → open directly in browser
    if (cleanUrl.endsWith(".pdf")) {
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📊 Office files → Office Viewer
    if (
      cleanUrl.endsWith(".xls") ||
      cleanUrl.endsWith(".xlsx") ||
      cleanUrl.endsWith(".doc") ||
      cleanUrl.endsWith(".docx") ||
      cleanUrl.endsWith(".ppt") ||
      cleanUrl.endsWith(".pptx")
    ) {
      const officeViewerUrl =
        "https://view.officeapps.live.com/op/view.aspx?src=" +
        encodeURIComponent(fileUrl);

      window.open(officeViewerUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // ⬇ Default → download / open
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };
  const [activeTab, setActiveTab] = useState("transactions");
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  // Data states
  const [customerDetails, setCustomerDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);
  // Loading states
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch customer details
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        const response = await viewPaymentAPI(customerId);
        if (response.success) {
          setCustomerDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching customer details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCustomerDetails();
    } else {
      // If no customer ID in state, redirect to payment list
      navigate("/payment");
    }
  }, [customerId, navigate]);
  const downloadFile = (fileUrl, fileName) => {
    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error("Download error:", error));
  };
  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const param = {
          limit: itemsPerPage,
          skip: itemOffset * itemsPerPage,
          search: searchQuery,
        };
        setTransactionsLoading(true);
        const response = await getPaymentTransactionsAPI(customerId, param);
        if (response.success) {
          setTotalCount(response.total_rec ?? 0);
          setPageCount(response.total_pgs ?? 0);
          setTransactions(response.transactions || []);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setTransactionsLoading(false);
      }
    };

    if (customerId && activeTab === "transactions") {
      fetchTransactions();
    }
  }, [customerId, searchQuery, itemsPerPage, itemOffset, activeTab]);

  // Fetch payment history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const response = await getPaymentHistoryAPI(customerId);
        if (response.success) {
          setPaymentHistory(response.histories || []);
        }
      } catch (error) {
      } finally {
        setHistoryLoading(false);
      }
    };

    if (customerId && activeTab === "history") {
      fetchHistory();
    }
  }, [customerId, activeTab]);

  // Filter transactions based on status and search
  // const filteredTransactions = transactions.filter((transaction) => {
  //   const matchesFilter =
  //     activeFilter === "all" ||
  //     (activeFilter === "pending" && transaction.status === "Pending") ||
  //     (activeFilter === "paid" && transaction.status === "Paid");

  //   const matchesSearch =
  //     searchQuery === "" ||
  //     transaction.transaction_id
  //       ?.toLowerCase()
  //       .includes(searchQuery.toLowerCase()) ||
  //     transaction.purchase_order
  //       ?.toLowerCase()
  //       .includes(searchQuery.toLowerCase());

  //   return matchesFilter && matchesSearch;
  // });

  // Filter history based on search
  const filteredHistory = paymentHistory.filter((history) => {
    return (
      searchQuery === "" ||
      history.payment_id?.toString().includes(searchQuery) ||
      history.payment_method?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Get customer initial for logo
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "V";
  };

  const renderPaymentHistory = () => {
    if (historyLoading) {
      return (
        <tr>
          <td
            colSpan="7"
            style={{ textAlign: "center", padding: "40px" }}
          >
            Loading history...
          </td>
        </tr>
      );
    }

    if (filteredHistory.length === 0) {
      return (
        <tr>
          <td
            colSpan="7"
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            No payment history found
          </td>
        </tr>
      );
    }

    return filteredHistory.map((history, index) => (
      <tr
        key={history.id}
        style={{
          borderBottom:
            index < filteredHistory.length - 1
              ? "1px solid #f5f5f7"
              : "none",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Transaction ID with Icon */}
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#fff",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={transactionIcon}
                alt="Transaction"
                style={{ width: "20px", height: "20px" }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                }}
              >
                {history.transaction_code || "-"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#6B7280",
                }}
              >
                {history.transaction_date ?? "-"}
              </div>
            </div>
          </div>
        </td>

        {/* Purchase Order */}
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#1a1a1a",
            }}
          >
            {history.po_code || "-"}
          </div>
        </td>

        {/* Total Amount */}
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#1a1a1a",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} /> {history.amount || 0}
          </div>
        </td>

        {/* Mode (Payment Method Badge) */}
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <span
            style={{
              backgroundColor: "#EDE9FE",
              color: "#7C3AED",
              padding: "4px 12px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            {history.payment_type || "-"}
          </span>
        </td>

        {/* Attachment */}
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          {history.attachment ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openFile(history.attachment);
              }}
            >
              {(() => {
                const filePath = history.attachment;
                const fileName = filePath
                  .split("/")
                  .pop();

                return (
                  <>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                        color: "#6B7280",
                      }}
                    >
                      {fileName.length > 20
                        ? fileName.substring(0, 20) +
                          "..."
                        : fileName}
                    </span>

                    <div
                      onClick={() => {
                        downloadFile(
                          history.attachment,
                          history.file_name ||
                            history.file.split("/").pop(),
                        );
                      }}
                      style={{
                        color: "#6B7280",
                        textDecoration: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                      title={`Download ${fileName}`}
                    >
                      <img
                        src={DownloadIc}
                        alt="Profile"
                        style={{
                          width: "14px",
                          height: "14px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <span
              style={{
                fontSize: "13px",
                fontWeight: "400",
                color: "#9CA3AF",
              }}
            >
              -
            </span>
          )}
        </td>
      </tr>
    ));
  };

  const renderTransactions = () => {
    if (transactionsLoading) {
      return (
        <tr>
          <td
            colSpan="6"
            style={{ textAlign: "center", padding: "40px" }}
          >
            Loading transactions...
          </td>
        </tr>
      );
    }

    if (transactions.length === 0) {
      return (
        <tr>
          <td
            colSpan="6"
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            No transactions found
          </td>
        </tr>
      );
    }

    return transactions.map((transaction, index) => (
      <tr
        key={transaction.id}
        style={{
          borderBottom:
            index < transactions.length - 1
              ? "1px solid #f5f5f7"
              : "none",
          backgroundColor: "#ffffff",
        }}
      >
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#eeeefd",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={transactionIcon}
                alt=""
                srcset=""
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  marginBottom: "2px",
                }}
              >
                {transaction.transaction_code || "-"}
              </div>
            </div>
          </div>
        </td>
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#1a1a1a",
            }}
          >
            {transaction.purchase_order || "-"}
          </div>
        </td>
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#1a1a1a",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} />{" "}
            {transaction.total_amount || 0}
          </div>
        </td>
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#34c759",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} />{" "}
            {transaction.paid_amount || 0}
          </div>
        </td>
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color:
                transaction.status === "Paid"
                  ? "#34c759"
                  : "#ff3b30",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} />{" "}
            {transaction.outstanding || 0}
          </div>
        </td>
        <td
          style={{
            padding: "16px 20px",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          {transaction.status === "Paid" ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  backgroundColor: "#34c759",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                >
                  <path
                    d="M1 4L3.5 6.5L9 1"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#1a1a1a",
                }}
              >
                Paid
              </span>
            </div>
          ) : (
            <span
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#ff3b30",
                backgroundColor: "#fff0f0",
                padding: "4px 12px",
                borderRadius: "12px",
              }}
            >
              Pending
            </span>
          )}
        </td>
      </tr>
    ));
  };

  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar currentfile="Payment" />
        <div className="rounded-4 flex-grow-1">
          <Navbar pageTitle="Payment Details" />
          <div className="bg-half-white p-2">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "70vh",
                backgroundColor: "#ffffff",
                borderRadius: "12px",
              }}
            >
              <div>Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <ProfileImageModal
        show={showProfilePopup}
        onClose={() => setShowProfilePopup(false)}
        imageUrl={selectedProfile}
      />{" "}
      {/* Sidebar */}
      <Sidebar currentfile="Payment" />
      {/* Main Content */}
      <div className="rounded-4 flex-grow-1">
        <Navbar pageTitle="Payment Details" />
        <div className="bg-half-white p-2">
          <div
            style={{
              backgroundColor: "#f5f5f7",
              minHeight: "100vh",
              paddingBottom: "30px",
            }}
          >
            {/* Header */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #e8e8e8",
                paddingTop: "20px",
                paddingBottom: "20px",
              }}
            >
              <Container
                fluid
                style={{ paddingLeft: "24px", paddingRight: "24px" }}
              >
                <Row className="align-items-center">
                  <Col xs="auto">
                    <Button
                      variant="link"
                      onClick={() => navigate("/payment")}
                      style={{
                        padding: "0",
                        color: "#000",
                        textDecoration: "none",
                        fontSize: "20px",
                      }}
                    >
                      <FaArrowLeft />
                    </Button>
                  </Col>
                  <Col xs="auto">
                    {customerDetails?.profile ? (
                      <img
                        src={customerDetails.profile}
                        alt="Profile"
                        className="rounded-circle"
                        style={{
                          width: "52px",
                          height: "52px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "52px",
                          height: "52px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontSize: "20px",
                          fontWeight: "700",
                        }}
                      >
                        {getInitial(customerDetails?.name)}
                      </div>
                    )}
                  </Col>
                  <Col>
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1a1a1a",
                          marginBottom: "2px",
                        }}
                      >
                        {customerDetails?.name || "-"}
                      </div>
                      <div style={{ fontSize: "13px", color: "#666666" }}>
                        Revenue :{" "}
                        <span style={{ color: "#5856d6", fontWeight: "500" }}>
                          <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} />{" "}
                          {customerDetails?.revenue || 0}
                        </span>
                        {" | "}
                        Outstanding :{" "}
                        <span style={{ color: "#ff3b30", fontWeight: "500" }}>
                          <span dangerouslySetInnerHTML={{ __html: customerDetails?.symbol || "&#8377;" }} />{" "}
                          {customerDetails?.outstanding || 0}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col xs="auto">
                    {customerDetails?.outstanding > 0 && (
                      <Button
                        onClick={() =>
                          navigate("/payment/create", {
                            state: {
                              vendor: {
                                label: customerDetails?.name,
                                value: customerId,
                              },
                            },
                          })
                        }
                        style={{
                          backgroundColor: "#5856d6",
                          border: "none",
                          borderRadius: "22px",
                          padding: "10px 28px",
                          fontSize: "14px",
                          fontWeight: "500",
                          boxShadow: "0 2px 8px rgba(88, 86, 214, 0.3)",
                        }}
                      >
                        New payment
                      </Button>
                    )}
                  </Col>
                </Row>
              </Container>
            </div>

            {/* Tabs */}
            <Container
              fluid
              style={{
                paddingLeft: "24px",
                paddingRight: "24px",
                marginTop: "24px",
                backgroundColor: "#fff",
                borderRadius: "12px",
              }}
            >
              <Nav variant="tabs" style={{ borderBottom: "1px solid #e8e8e8" }}>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "transactions"}
                    onClick={() => setActiveTab("transactions")}
                    style={{
                      color:
                        activeTab === "transactions" ? "#5856d6" : "#666666",
                      fontWeight: activeTab === "transactions" ? "600" : "500",
                      borderBottom:
                        activeTab === "transactions"
                          ? "2px solid #5856d6"
                          : "none",
                      backgroundColor: "transparent",
                      border: "none",
                      paddingLeft: "0",
                      paddingRight: "32px",
                      paddingBottom: "12px",
                      fontSize: "15px",
                    }}
                  >
                    Transactions
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "history"}
                    onClick={() => setActiveTab("history")}
                    style={{
                      color: activeTab === "history" ? "#5856d6" : "#666666",
                      fontWeight: activeTab === "history" ? "600" : "500",
                      borderBottom:
                        activeTab === "history" ? "2px solid #5856d6" : "none",
                      backgroundColor: "transparent",
                      border: "none",
                      paddingLeft: "0",
                      paddingRight: "0",
                      paddingBottom: "12px",
                      fontSize: "15px",
                    }}
                  >
                    Payment History
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Filters and Search */}
              {activeTab !== "history" && (
                <Row
                  className="align-items-center"
                  style={{
                    marginTop: "24px",
                    marginBottom: "24px",
                    backgroundColor: "#fff",
                  }}
                >
                  <Col>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <Button
                        onClick={() => setActiveFilter("all")}
                        style={{
                          backgroundColor:
                            activeFilter === "all" ? "#1a1a1a" : "#ffffff",
                          color: activeFilter === "all" ? "#ffffff" : "#1a1a1a",
                          border:
                            activeFilter === "all"
                              ? "none"
                              : "1px solid #e8e8e8",
                          borderRadius: "20px",
                          padding: "8px 20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          boxShadow: "none",
                        }}
                      >
                        All
                      </Button>
                      <Button
                        onClick={() => setActiveFilter("pending")}
                        style={{
                          backgroundColor:
                            activeFilter === "pending" ? "#1a1a1a" : "#ffffff",
                          color:
                            activeFilter === "pending" ? "#ffffff" : "#1a1a1a",
                          border:
                            activeFilter === "pending"
                              ? "none"
                              : "1px solid #e8e8e8",
                          borderRadius: "20px",
                          padding: "8px 20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          boxShadow: "none",
                        }}
                      >
                        Pending
                      </Button>
                      <Button
                        onClick={() => setActiveFilter("paid")}
                        style={{
                          backgroundColor:
                            activeFilter === "paid" ? "#1a1a1a" : "#ffffff",
                          color:
                            activeFilter === "paid" ? "#ffffff" : "#1a1a1a",
                          border:
                            activeFilter === "paid"
                              ? "none"
                              : "1px solid #e8e8e8",
                          borderRadius: "20px",
                          padding: "8px 20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          boxShadow: "none",
                        }}
                      >
                        Paid
                      </Button>
                    </div>
                  </Col>
                  <Col xs="auto">
                    <div style={{ position: "relative", width: "320px" }}>
                      <FaSearch
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#999999",
                          zIndex: 10,
                        }}
                      />
                      <Form.Control
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          border: "1px solid #e8e8e8",
                          borderRadius: "8px",
                          padding: "10px 40px 10px 16px",
                          fontSize: "14px",
                          backgroundColor: "#ffffff",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              )}

              {/* Transactions Tab */}

              {activeTab === "transactions" && (
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      overflowX: "auto",
                      maxHeight: "56vh",
                      overflowY: "auto",
                    }}
                  >
                    <Table hover style={{ marginBottom: "0" }}>
                      <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr
                          style={{
                            backgroundColor: "#ffffff",
                            borderBottom: "1px solid #e8e8e8",
                          }}
                        >
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Transaction ID
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Purchase Order
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Total Amount
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Paid
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Outstanding
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                              border: "none",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderTransactions()}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
              {activeTab === "transactions" && (
                <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <Dropdown>
                    <span className="pagination-color">
                      Records per page: &nbsp;
                    </span>
                    <Dropdown.Toggle
                      id="dropdown-basic"
                      className="bg-transparent pagination-color  border-0 custom-dropdown-toggle p-0"
                    >
                      <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                        {itemsPerPage} <DownArrowIc />
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="my-2"
                      style={{ minWidth: "unset", width: "30%" }}
                    >
                      {[5, 10, 25, 50, 75, 100].map((size) => (
                        <Dropdown.Item
                          key={size}
                          onClick={() => {
                            setItemsPerPage(size);
                          }}
                          active={itemsPerPage === size}
                          className="text-center"
                        >
                          {size}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <div className="d-flex align-items-center gap-2">
                    <ReactPaginate
                      previousLabel={
                        <div
                          className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                          }}
                        >
                          <LarIc />
                        </div>
                      }
                      nextLabel={
                        <div
                          className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                          }}
                        >
                          <RarIc />
                        </div>
                      }
                      breakLabel={"..."}
                      pageCount={pageCount + 1}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={3}
                      onPageChange={handlePageClick}
                      containerClassName={"pagination"}
                      activeClassName={"active"}
                    />
                  </div>
                </div>
              )}
              {/* Payment History Tab */}
              {activeTab === "history" && (
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    overflow: "hidden",
                    marginTop: "10px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      overflowX: "auto",
                      maxHeight: "56vh",
                      overflowY: "auto",
                    }}
                  >
                    <Table hover style={{ marginBottom: "0" }}>
                      <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr
                          style={{
                            backgroundColor: "#F8F9FA",
                            borderBottom: "1px solid #e8e8e8",
                          }}
                        >
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#6B7280",
                              border: "none",
                              cursor: "pointer",
                              backgroundColor: "#fff",
                            }}
                          >
                            Transaction ID
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#6B7280",
                              border: "none",
                              cursor: "pointer",
                              backgroundColor: "#fff",
                            }}
                          >
                            Purchase Order
                          </th>

                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#6B7280",
                              border: "none",
                              cursor: "pointer",
                              backgroundColor: "#fff",
                            }}
                          >
                            Amount
                          </th>
                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#6B7280",
                              border: "none",
                              cursor: "pointer",
                              backgroundColor: "#fff",
                            }}
                          >
                            Mode
                          </th>

                          <th
                            style={{
                              padding: "16px 20px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#6B7280",
                              border: "none",
                              backgroundColor: "#fff",
                            }}
                          >
                            Attachment
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderPaymentHistory()}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Container>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentView;
